import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI } from '@/utils/api';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  User,
  FileText,
  FileCheck,
  Receipt,
  Users,
  FolderKanban,
  ClipboardList,
  Banknote,
  TrendingUp,
  LogOut,
  FileSpreadsheet,
  Settings,
  BarChart3,
  Mail,
  Link2,
  Clock,
  Bell,
  UserPlus,
  Briefcase,
  DollarSign,
  FileBarChart,
} from 'lucide-react';

const DashboardLayout = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeMode } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'owner') {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };
  
  const handleLogout = async () => {
    try {
      await authAPI.logout();
      localStorage.removeItem('session_token');
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  const getNavItems = () => {
    if (user?.role === 'translator') {
      return [
        { section: 'OVERVIEW' },
        { path: '/translator', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/translator/profile', icon: User, label: 'Profile' },
        { path: '/translator/nda', icon: FileCheck, label: 'NDA' },
        { section: 'WORK' },
        { path: '/translator/assignments', icon: ClipboardList, label: 'Assignments' },
        { path: '/translator/work', icon: Briefcase, label: 'My Work' },
        { path: '/translator/invoices', icon: Receipt, label: 'Invoices' },
      ];
    } else if (user?.role === 'admin') {
      return [
        { section: 'OVERVIEW' },
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/admin/in-progress', icon: Clock, label: "What's in Progress" },
        { section: 'TEAM' },
        { path: '/admin/translators', icon: Users, label: 'Translators' },
        { path: '/admin/invite', icon: UserPlus, label: 'Invite' },
        { path: '/admin/contributors', icon: BarChart3, label: 'Contributors' },
        { section: 'DATA' },
        { path: '/admin/team-projects', icon: Briefcase, label: 'Team Projects' },
        { path: '/admin/projects-bills', icon: FileBarChart, label: 'Projects & Bills' },
        { path: '/admin/client-projects', icon: FolderKanban, label: 'Client Projects' },
        { section: 'TOOLS' },
        { path: '/admin/gts-pro-emails', icon: Mail, label: 'GTS Pro Emails' },
        { path: '/admin/map-pro', icon: Link2, label: 'MapPro' },
        { section: 'BILLING' },
        { path: '/admin/assignments', icon: ClipboardList, label: 'Assignments' },
        { path: '/admin/submissions', icon: FileCheck, label: 'Submissions' },
        { path: '/admin/invoices', icon: Receipt, label: 'Invoices' },
      ];
    } else if (user?.role === 'owner') {
      return [
        { section: 'OVERVIEW' },
        { path: '/owner', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/owner/finance', icon: TrendingUp, label: 'Finance' },
        { path: '/owner/in-progress', icon: Clock, label: "What's in Progress" },
        { section: 'TEAM & REVENUE' },
        { path: '/admin/invite', icon: UserPlus, label: 'Invite' },
        { path: '/admin/team-projects', icon: Briefcase, label: 'Team Projects' },
        { path: '/owner/projects-bills', icon: FileBarChart, label: 'Projects & Bills' },
        { path: '/owner/team-revenue', icon: DollarSign, label: 'Team Revenue' },
        { path: '/admin/contributors', icon: BarChart3, label: 'Contributors' },
        { section: 'DATA MANAGEMENT' },
        { path: '/owner/projects', icon: FolderKanban, label: 'Client Projects' },
        { path: '/owner/billings', icon: FileSpreadsheet, label: 'Client Billings' },
        { section: 'TOOLS' },
        { path: '/owner/gts-pro', icon: Mail, label: 'GTS Pro Emails' },
        { path: '/admin/map-pro', icon: Link2, label: 'MapPro' },
        { section: 'SETTINGS' },
        { path: '/owner/payments', icon: Banknote, label: 'Payments' },
        { path: '/owner/settings', icon: Settings, label: 'Settings' },
        { path: '/admin', icon: Users, label: 'Admin Panel' },
      ];
    }
    return [];
  };
  
  const navItems = getNavItems();
  
  const isGlass = themeMode === 'glass';
  
  return (
    <div className={cn(
      "flex min-h-screen",
      isGlass 
        ? "bg-gradient-to-br from-slate-50 via-violet-50/30 to-purple-50/30" 
        : "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800"
    )}>
      {/* Sidebar - Vibrant Dark */}
      <motion.aside 
        className="sidebar-vibrant w-64 flex flex-col flex-shrink-0 shadow-2xl"
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="p-6 border-b border-white/10">
          <motion.h1 
            className="text-2xl font-heading font-bold bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Graphikpro
          </motion.h1>
          <p className="text-xs text-white/40 mt-1 tracking-wider uppercase">Translation Portal</p>
        </div>
        
        <nav className="p-3 space-y-0.5 flex-1 overflow-y-auto">
          {navItems.map((item, index) => {
            if (item.section) {
              return (
                <div key={`section-${item.section}`} className="pt-4 pb-1 px-4 first:pt-1">
                  <p className="text-[10px] font-semibold tracking-[0.15em] text-white/30 uppercase">{item.section}</p>
                </div>
              );
            }
            
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <motion.div
                key={item.path}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 + index * 0.03 }}
              >
                <Link
                  to={item.path}
                  className={cn("nav-item", isActive && "active")}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[ ']/g, '-')}`}
                >
                  <Icon className="w-[18px] h-[18px]" />
                  {item.label}
                </Link>
              </motion.div>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-white/10">
          <motion.div 
            className="flex items-center gap-3 px-3 py-2 mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm bg-gradient-to-br from-violet-500 to-pink-500 shadow-lg shadow-violet-500/30">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white/90 truncate">{user?.name}</p>
              <p className="text-[10px] text-white/40 capitalize tracking-wider">{user?.role}</p>
            </div>
          </motion.div>
          {/* Notification Bell */}
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start rounded-xl relative text-white/60 hover:text-white hover:bg-white/10"
                onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs && unreadCount > 0) markAllRead(); }}
                data-testid="notification-bell"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {unreadCount > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
              {showNotifs && (
                <div className="absolute bottom-full left-0 w-72 mb-2 bg-popover border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                  {notifications.length === 0 ? (
                    <p className="text-xs text-muted-foreground p-4 text-center">No notifications</p>
                  ) : (
                    notifications.slice(0, 10).map(n => (
                      <div key={n.notif_id} className={`p-3 border-b last:border-0 text-xs ${n.read ? '' : 'bg-violet-50 dark:bg-violet-900/20'}`}>
                        <p className="font-medium">{n.title}</p>
                        <p className="text-muted-foreground mt-0.5">{n.message}</p>
                        <p className="text-muted-foreground mt-1 text-[10px]">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start rounded-xl border-white/10 text-white/60 hover:text-white hover:bg-white/10"
              data-testid="btn-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </motion.div>
        </div>
      </motion.aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-background">
        <motion.div 
          className="p-6 md:p-8 lg:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
};

export default DashboardLayout;
